import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Home, FileText, Receipt, BarChart3, Settings, Plus, Search,
  TrendingUp, TrendingDown, Users, CreditCard, Bell, RefreshCw,
  Camera, QrCode, ChevronRight, Calendar, ArrowUpRight, ArrowDownLeft,
  Briefcase, PieChart, Wallet, Scan, Building2, WifiOff
} from 'lucide-react';
import '../styles/mobile.css';
import { useOffline } from '../hooks/useOffline';
import { OfflineIndicator, StaleDataIndicator } from '../components/OfflineIndicator';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (amount, currency = 'SEK') => {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
};

export default function MobileDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOffline, isOnline } = useOffline();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isStale, setIsStale] = useState(false);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    unpaidInvoices: 0,
    unpaidAmount: 0,
    customerCount: 0,
    recentInvoices: [],
    recentTransactions: []
  });
  const [activeNav, setActiveNav] = useState('home');

  const token = localStorage.getItem('token');
  const CACHE_KEY = 'yubi_mobile_dashboard';

  // Load cached data on mount
  useEffect(() => {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        setStats(data);
        // Check if data is older than 5 minutes
        const age = Date.now() - timestamp;
        setIsStale(age > 5 * 60 * 1000);
      } catch (e) {
        console.error('Error loading cached dashboard:', e);
      }
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!token) return;
    
    // Don't fetch if offline - use cached data
    if (!navigator.onLine) {
      setLoading(false);
      setRefreshing(false);
      return;
    }
    
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch dashboard stats
      const [invoicesRes, customersRes, entriesRes] = await Promise.all([
        fetch(`${API_BASE}/api/invoices`, { headers }),
        fetch(`${API_BASE}/api/customers`, { headers }),
        fetch(`${API_BASE}/api/journal-entries?limit=5`, { headers })
      ]);

      const invoices = await invoicesRes.json();
      const customers = await customersRes.json();
      const entries = await entriesRes.json();

      const invoiceList = Array.isArray(invoices) ? invoices : [];
      const customerList = Array.isArray(customers) ? customers : [];
      const entryList = Array.isArray(entries) ? entries : [];

      const paidInvoices = invoiceList.filter(i => i.status === 'paid');
      const unpaidInvoices = invoiceList.filter(i => ['sent', 'overdue'].includes(i.status));

      const newStats = {
        totalRevenue: paidInvoices.reduce((sum, i) => sum + (i.total || 0), 0),
        unpaidInvoices: unpaidInvoices.length,
        unpaidAmount: unpaidInvoices.reduce((sum, i) => sum + (i.total || 0), 0),
        customerCount: customerList.length,
        recentInvoices: invoiceList.slice(0, 5),
        recentTransactions: entryList.slice(0, 5)
      };

      setStats(newStats);
      setIsStale(false);
      
      // Cache the data
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data: newStats,
        timestamp: Date.now()
      }));
    } catch (err) {
      console.error('Error fetching mobile data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, CACHE_KEY]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
  };

  const quickActions = [
    { icon: <Plus size={20} />, label: t('dashboard.newInvoice'), color: '#e0f2fe', iconColor: '#0284c7', action: () => navigate('/m/invoices/new') },
    { icon: <Camera size={20} />, label: t('dashboard.scanInvoice'), color: '#dcfce7', iconColor: '#16a34a', action: () => navigate('/m/scan') },
    { icon: <Receipt size={20} />, label: t('bookkeeping.newEntry'), color: '#f3e8ff', iconColor: '#9333ea', action: () => navigate('/m/bookkeeping/new') },
    { icon: <Users size={20} />, label: t('customers.newCustomer'), color: '#ffedd5', iconColor: '#ea580c', action: () => navigate('/m/customers/new') },
  ];

  const getStatusBadge = (status) => {
    const statusMap = {
      paid: { class: 'success', label: t('invoices.status.paid') },
      sent: { class: 'info', label: t('invoices.status.sent') },
      overdue: { class: 'danger', label: t('invoices.status.overdue') },
      draft: { class: 'neutral', label: t('invoices.status.draft') }
    };
    const s = statusMap[status] || statusMap.draft;
    return <span className={`mobile-badge ${s.class}`}>{s.label}</span>;
  };

  if (!token) {
    return (
      <div className="mobile-app">
        <div className="mobile-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
          <div className="mobile-card" style={{ textAlign: 'center', padding: '32px' }}>
            <Building2 size={48} style={{ color: '#1e3a5f', marginBottom: '16px' }} />
            <h2 style={{ marginBottom: '8px' }}>Yubi.ai</h2>
            <p style={{ color: '#64748b', marginBottom: '24px' }}>{t('auth.login')}</p>
            <button className="mobile-btn mobile-btn-primary" onClick={() => navigate('/login')}>
              {t('auth.login')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-app">
      {/* Offline Indicator */}
      <OfflineIndicator />
      
      {/* Header */}
      <header className="mobile-header">
        <div className="mobile-header-title">
          <Briefcase size={24} />
          <span>Yubi.ai</span>
          {isOffline && <WifiOff size={16} className="ml-2 text-red-300" />}
        </div>
        <div className="mobile-header-actions">
          <button 
            className="mobile-header-btn" 
            onClick={handleRefresh} 
            disabled={refreshing || isOffline}
            style={{ opacity: isOffline ? 0.5 : 1 }}
          >
            <RefreshCw size={20} className={refreshing ? 'mobile-spinner' : ''} />
          </button>
          <button className="mobile-header-btn" onClick={() => navigate('/m/notifications')}>
            <Bell size={20} />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="mobile-content">
        {/* Stale Data Warning */}
        <StaleDataIndicator isStale={isStale} onRefresh={handleRefresh} loading={refreshing} />
        
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <div className="mobile-spinner" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="mobile-stats-grid mobile-animate-up">
              <div className="mobile-stat-card">
                <div className="mobile-stat-icon green">
                  <TrendingUp size={24} />
                </div>
                <div className="mobile-stat-value">{formatCurrency(stats.totalRevenue)}</div>
                <div className="mobile-stat-label">{t('dashboard.revenue')}</div>
              </div>
              <div className="mobile-stat-card">
                <div className="mobile-stat-icon orange">
                  <CreditCard size={24} />
                </div>
                <div className="mobile-stat-value">{formatCurrency(stats.unpaidAmount)}</div>
                <div className="mobile-stat-label">{t('dashboard.unpaid')}</div>
              </div>
              <div className="mobile-stat-card">
                <div className="mobile-stat-icon blue">
                  <FileText size={24} />
                </div>
                <div className="mobile-stat-value">{stats.unpaidInvoices}</div>
                <div className="mobile-stat-label">{t('dashboard.invoices')}</div>
              </div>
              <div className="mobile-stat-card">
                <div className="mobile-stat-icon purple">
                  <Users size={24} />
                </div>
                <div className="mobile-stat-value">{stats.customerCount}</div>
                <div className="mobile-stat-label">{t('dashboard.customers')}</div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mobile-section-header">
              <h3 className="mobile-section-title">{t('dashboard.quickActions')}</h3>
            </div>
            <div className="mobile-quick-actions mobile-animate-up" style={{ animationDelay: '0.1s' }}>
              {quickActions.map((action, idx) => (
                <button key={idx} className="mobile-quick-action" onClick={action.action}>
                  <div className="mobile-quick-action-icon" style={{ background: action.color, color: action.iconColor }}>
                    {action.icon}
                  </div>
                  <span className="mobile-quick-action-label">{action.label}</span>
                </button>
              ))}
            </div>

            {/* Recent Invoices */}
            <div className="mobile-section-header">
              <h3 className="mobile-section-title">{t('dashboard.recentInvoices')}</h3>
              <button className="mobile-section-action" onClick={() => navigate('/m/invoices')}>
                {t('dashboard.viewAll')}
              </button>
            </div>
            <div className="mobile-list mobile-animate-up" style={{ animationDelay: '0.2s' }}>
              {stats.recentInvoices.length === 0 ? (
                <div className="mobile-empty">
                  <div className="mobile-empty-icon"><FileText size={48} /></div>
                  <div className="mobile-empty-title">{t('dashboard.noInvoices')}</div>
                </div>
              ) : (
                stats.recentInvoices.map((invoice, idx) => (
                  <div key={idx} className="mobile-list-item" onClick={() => navigate(`/m/invoices/${invoice.id}`)}>
                    <div className="mobile-list-icon" style={{ background: '#e0f2fe', color: '#0284c7' }}>
                      <FileText size={20} />
                    </div>
                    <div className="mobile-list-content">
                      <div className="mobile-list-title">{invoice.customer_name || 'Kund'}</div>
                      <div className="mobile-list-subtitle">#{invoice.invoice_number} • {formatDate(invoice.invoice_date)}</div>
                    </div>
                    <div className="mobile-list-value">
                      <div className="mobile-list-amount">{formatCurrency(invoice.total)}</div>
                      {getStatusBadge(invoice.status)}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Recent Transactions */}
            <div className="mobile-section-header" style={{ marginTop: '8px' }}>
              <h3 className="mobile-section-title">{t('bookkeeping.title')}</h3>
              <button className="mobile-section-action" onClick={() => navigate('/m/bookkeeping')}>
                {t('dashboard.viewAll')}
              </button>
            </div>
            <div className="mobile-list mobile-animate-up" style={{ animationDelay: '0.3s' }}>
              {stats.recentTransactions.length === 0 ? (
                <div className="mobile-empty">
                  <div className="mobile-empty-icon"><Receipt size={48} /></div>
                  <div className="mobile-empty-title">{t('common.noData')}</div>
                </div>
              ) : (
                stats.recentTransactions.map((entry, idx) => (
                  <div key={idx} className="mobile-list-item" onClick={() => navigate(`/m/bookkeeping/${entry.id}`)}>
                    <div className="mobile-list-icon" style={{ background: '#f3e8ff', color: '#9333ea' }}>
                      <Receipt size={20} />
                    </div>
                    <div className="mobile-list-content">
                      <div className="mobile-list-title">{entry.description || `Verifikation #${entry.entry_number}`}</div>
                      <div className="mobile-list-subtitle">{formatDate(entry.date)}</div>
                    </div>
                    <div className="mobile-list-value">
                      <div className="mobile-list-amount">{formatCurrency(entry.total_debit || 0)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </main>

      {/* Floating Action Button */}
      <button className="mobile-fab" onClick={() => navigate('/m/invoices/new')}>
        <Plus size={24} />
      </button>

      {/* Bottom Navigation */}
      <nav className="mobile-nav">
        <button className={`mobile-nav-item ${activeNav === 'home' ? 'active' : ''}`} onClick={() => { setActiveNav('home'); navigate('/m'); }}>
          <Home size={22} className="mobile-nav-icon" />
          <span className="mobile-nav-label">{t('nav.dashboard')}</span>
        </button>
        <button className={`mobile-nav-item ${activeNav === 'invoices' ? 'active' : ''}`} onClick={() => { setActiveNav('invoices'); navigate('/m/invoices'); }}>
          <FileText size={22} className="mobile-nav-icon" />
          <span className="mobile-nav-label">{t('nav.invoices')}</span>
        </button>
        <button className={`mobile-nav-item ${activeNav === 'scan' ? 'active' : ''}`} onClick={() => { setActiveNav('scan'); navigate('/m/scan'); }}>
          <Scan size={22} className="mobile-nav-icon" />
          <span className="mobile-nav-label">{t('nav.invoiceScanner')}</span>
        </button>
        <button className={`mobile-nav-item ${activeNav === 'reports' ? 'active' : ''}`} onClick={() => { setActiveNav('reports'); navigate('/m/reports'); }}>
          <BarChart3 size={22} className="mobile-nav-icon" />
          <span className="mobile-nav-label">{t('nav.reports')}</span>
        </button>
        <button className={`mobile-nav-item ${activeNav === 'more' ? 'active' : ''}`} onClick={() => { setActiveNav('more'); navigate('/m/more'); }}>
          <Settings size={22} className="mobile-nav-icon" />
          <span className="mobile-nav-label">{t('nav.settings')}</span>
        </button>
      </nav>
    </div>
  );
}
